'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getAdventureById } from '@/lib/data'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/free-mode'
import styles from '@/styles/adventure-page.module.css'
import { BreadcrumbSchema, AdventureSchema } from '@/lib/schema'
import { useSwiperCleanup } from '@/hooks/useSwiperCleanup'
import BookNowCTA from '@/components/BookNowCTA'


export default function ResortSkiingPage() {
  const swiperRef = useSwiperCleanup()
  const thisAdventure = getAdventureById('resort-skiing')
  const t = useTranslations('guidedAdventures')
  const tAdv = useTranslations('adventures')
  const tCommon = useTranslations('common')
  const tLodges = useTranslations('lodges')
  const params = useParams()
  const locale = params.locale as string

  if (!thisAdventure) {
    return <div>{t('common.adventureNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/guided-adventures/` },
        { name: tAdv('resort-skiing.title'), href: `/${locale}/guided-adventures/resort-skiing/` },
      ]} />
      <AdventureSchema
        name={t('resortSkiing.schema.name')}
        description={t('resortSkiing.schema.description')}
        image={thisAdventure.heroImage}
        url={`/${locale}/guided-adventures/resort-skiing/`}
        activityType={t('resortSkiing.schema.activityType')}
        location={t('resortSkiing.schema.location')}
        geo={{ latitude: -33.33, longitude: -70.28 }}
      />
      <Header />

      <HeroParallax
        backgroundImage={thisAdventure.heroImage}
        title={tAdv('resort-skiing.title')}
        subtitle={tAdv('resort-skiing.subtitle')}
        location={tAdv('resort-skiing.location')}
      />

      <main className={styles.pageWrapper}>
        <section className={styles.adventureDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              <div className={styles.itineraryColumn}>
                <h3>{t('resortSkiing.servicesTitle')}</h3>
                <p className={styles.serviceDescription}>{t('resortSkiing.descriptionText')}</p>
              </div>

              <div className={styles.mapColumn}>
                <div className={styles.mapWrapper}>
                  <div style={{
                    width: '100%',
                    height: '480px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  }}>
                    <img
                      src="/images/adventures/resort-skiing/resort-skiing-hero-2.webp"
                      alt="Skier carving through powder at La Parva resort at sunset"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.includedColumn}>
                <h3>{t('resortSkiing.includedTitle')}</h3>
                <ul>
                  <li><strong>{t('resortSkiing.services.item1Label')}:</strong> {t('resortSkiing.services.item1Body')}</li>
                  <li><strong>{t('resortSkiing.services.item2Label')}:</strong> {t('resortSkiing.services.item2Body')}</li>
                  <li><strong>{t('resortSkiing.services.item3Label')}:</strong> {t('resortSkiing.services.item3Body')}</li>
                  <li><strong>{t('resortSkiing.services.item4Label')}:</strong> {t('resortSkiing.services.item4Body')}</li>
                  <li>{t('resortSkiing.services.item5')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="inline"
          productId="resort-skiing"
          productType="adventure"
          productName={tAdv('resort-skiing.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/resort-skiing"
        />

        <section className={styles.ikonSection}>
          <div className="container">
            <div className={styles.ikonGrid}>
              <div className={styles.ikonContent}>
                <span className={styles.ikonLabel}>{t('resortSkiing.ikonLabel')}</span>
                <h2 className={styles.ikonTitle}>{t('resortSkiing.ikonTitle')}</h2>
                <p className={styles.ikonBody}>{t('resortSkiing.ikonBody')}</p>

                <h4 className={styles.ikonBenefitsTitle}>{t('resortSkiing.ikonBenefitsTitle')}</h4>
                <ul className={styles.ikonBenefitsList}>
                  <li>{t('resortSkiing.ikonBenefit1')}</li>
                  <li>{t('resortSkiing.ikonBenefit2')}</li>
                  <li>{t('resortSkiing.ikonBenefit3')}</li>
                </ul>

                <p className={styles.ikonCta}>{t('resortSkiing.ikonCta')}</p>
                <p className={styles.ikonDisclaimer}>{t('resortSkiing.ikonDisclaimer')}</p>
              </div>

              <div className={styles.ikonImageWrapper}>
                <img
                  src="/images/adventures/resort-skiing/4.webp"
                  alt="Skier carving downhill at a Chilean Andes resort"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className={styles.adventures}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('common.personalizedAdventures')}</h2>
              <p className={styles.sectionDescription}>
                {t('common.personalizedDescription')}
              </p>
            </div>

            <div className={styles.adventuresCarousel}>
              <Swiper
                modules={[Navigation]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: `.${styles.swiperButtonNext}`,
                  prevEl: `.${styles.swiperButtonPrev}`,
                }}
                breakpoints={{
                  640: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                onSwiper={(swiper) => { swiperRef.current = swiper }}
                className={styles.adventuresSwiper}
              >
                {[
                  { id: 1, src: '/images/adventures/resort-skiing/2.webp', alt: t('resortSkiing.alts.gallery1') },
                  { id: 2, src: '/images/adventures/resort-skiing/4.webp', alt: t('resortSkiing.alts.gallery2') },
                  { id: 3, src: '/images/adventures/resort-skiing/5.webp', alt: t('resortSkiing.alts.gallery3') },
                  { id: 4, src: '/images/adventures/resort-skiing/6.webp', alt: t('resortSkiing.alts.gallery4') },
                ].map((image) => (
                  <SwiperSlide key={`resort-skiing-${image.id}`}>
                    <article className={styles.adventureCard}>
                      <div className={styles.adventureImage}>
                        <img src={image.src} alt={image.alt} loading="lazy" />
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className={`${styles.swiperButtonPrev} swiper-button-prev`}></div>
              <div className={`${styles.swiperButtonNext} swiper-button-next`}></div>
            </div>

            <div className={styles.sectionCta}>
              <a href={`/${locale}/guided-adventures/`} className="btn-primary">
                {t('common.exploreAdventures')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="section"
          productId="resort-skiing"
          productType="adventure"
          productName={tAdv('resort-skiing.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/resort-skiing"
        />

        <section className={styles.creamSection}>
          <div className="container">
            <h2>{t('common.whereToStay')}</h2>
            <div className={styles.lodgesGrid}>
              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-mountain-penthouse/IMG_2891.webp"
                    alt={`${tLodges('apex-mountain-penthouse.name')} - Ski-in ski-out penthouse in La Parva, Chile`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-mountain-penthouse.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-mountain-penthouse.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-mountain-penthouse/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>

              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=1920&q=80"
                    alt={`${tLodges('apex-parva-cave.name')} - Exclusive ski-in ski-out mountain retreat in La Parva`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-parva-cave.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-parva-cave.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-parva-cave/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <BookNowCTA
          variant="closing"
          productId="resort-skiing"
          productType="adventure"
          productName={tAdv('resort-skiing.title')}
          bookingMode={thisAdventure?.bookingMode || 'direct'}
          bookingUrl="/booking/activities/resort-skiing"
        />

      </main>

      <Footer />
    </>
  )
}
